import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import {
  getCaucionTasas,
  parseCaucionFile,
  uploadCaucionTasas,
} from '@/shared/api/controllers/caucionTasas';
import type { EntityViewProps } from '../../types';

/**
 * Tasas de Caución — carga de la serie diaria que alimenta la columna
 * Caución del Estado Financiero.
 *
 * No es un ABM fila por fila a propósito: la serie tiene cientos de ruedas
 * y el equipo financiero ya la mantiene en Excel. Lo único que hace falta
 * acá es subir el archivo y ver qué cobertura quedó cargada.
 */
export function CaucionTasasEntity(_props: EntityViewProps) {
  const { config: activeCountry } = useActiveCountry();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [subiendo, setSubiendo] = useState(false);

  const queryKey = ['configuracion', 'caucion-tasas', activeCountry.code];
  const q = useQuery({ queryKey, queryFn: getCaucionTasas, staleTime: 60_000 });

  const subir = useMutation({
    mutationFn: uploadCaucionTasas,
    onSuccess: (r) => {
      toast.success(`${r.cargadas} tasas cargadas.`);
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e) =>
      toast.error('No se pudo cargar la serie', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      }),
  });

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const tasas = await parseCaucionFile(file);
      if (tasas.length === 0) {
        toast.error('No se encontraron tasas en el archivo', {
          description: 'Se esperan columnas Fecha y Tasa_Diaria, o un JSON con { tasas: {...} }.',
        });
        return;
      }
      await subir.mutateAsync(tasas);
    } catch (e) {
      toast.error('No se pudo leer el archivo', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    } finally {
      setSubiendo(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const ocupado = subiendo || subir.isPending;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h2 className="text-lg font-semibold">Tasas de Caución</h2>
          <p className="text-sm text-muted-foreground">
            Serie diaria que alimenta la columna Caución del Estado Financiero. Para
            una fecha rige la última tasa cargada anterior o igual, así que sólo
            hacen falta las ruedas, no todos los días.
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.json"
          className="hidden"
          onChange={(e) => void onFile(e.target.files?.[0])}
        />
        <Button onClick={() => inputRef.current?.click()} disabled={ocupado} className="gap-2">
          {ocupado ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Upload className="h-4 w-4" aria-hidden />
          )}
          Cargar serie (.xlsx / .json)
        </Button>
      </div>

      {q.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : q.isError ? (
        <p className="text-sm text-destructive">No se pudo leer la serie cargada.</p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <Resumen label="Ruedas cargadas" valor={q.data.total.toLocaleString('es-AR')} />
            <Resumen label="Desde" valor={q.data.desde ?? '—'} />
            <Resumen label="Hasta" valor={q.data.hasta ?? '—'} />
            <Resumen
              label="Última tasa"
              valor={
                q.data.ultimaTasa == null
                  ? '—'
                  : `${(q.data.ultimaTasa * 100).toFixed(4).replace('.', ',')}% / día`
              }
            />
          </div>

          {q.data.total === 0 && (
            <p className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
              Sin tasas cargadas: la columna Caución del Estado Financiero muestra 0.
            </p>
          )}

          {q.data.recientes.length > 0 && (
            <div className="max-h-80 overflow-y-auto rounded-2xl border bg-card">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Fecha</th>
                    <th className="px-4 py-2 text-right">Tasa diaria</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.recientes.map((t) => (
                    <tr key={t.fecha} className="border-b last:border-b-0">
                      <td className="px-4 py-2">{t.fecha}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {`${(t.tasaDiaria * 100).toFixed(4).replace('.', ',')}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Resumen({ label, valor }: { label: string; valor: string }) {
  return (
    <div className="rounded-2xl border bg-card px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{valor}</p>
    </div>
  );
}
